import type { AgentTokenPublic } from 'types';
import type { AgentTokenExpiryDays, AgentTokenScope } from 'definitions/agent-tokens';
import type { AuthenticationResponseJSON, PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { startAuthentication } from '@simplewebauthn/browser';
import { useClient } from 'hooks/useClient';

export type AgentTokenOwner = { id: number; firstName: string | null; lastName: string | null; email: string };

export type TeamAgentToken = AgentTokenPublic & { owner: AgentTokenOwner };

export type AgentTokenWebsiteOption = { id: number; url: string; companyId: number; companyName: string };

export type AgentTokenOptions = {
  scopes: AgentTokenScope[];
  expiryDays: AgentTokenExpiryDays[];
  defaultExpiryDays: AgentTokenExpiryDays;
  maxActiveTokens: number;
  websites: AgentTokenWebsiteOption[];
  companies: { id: number; name: string }[];
  /** The account has a passkey, so creating a token asks for it. */
  passkeyStepUp: boolean;
  /** One of its passkeys is on this site, so the step-up can be given here. */
  passkeyUsableHere: boolean;
  /** The origins the account has passkeys on (never the credentials). */
  passkeyOrigins: string[];
};

export type NewAgentToken = {
  name: string;
  scopes: AgentTokenScope[];
  websiteIds: number[] | null;
  expiresInDays: AgentTokenExpiryDays;
  /** The caller's current password: making a token asks for it again. */
  password: string;
  /** A passkey assertion, for an account with passkeys (confirmWithPasskey below). */
  passkey?: AuthenticationResponseJSON;
};

type CreatedAgentToken = { token: AgentTokenPublic; secret: string };

const MINE_KEY = ['AGENT_TOKENS', 'mine'];
const TEAM_KEY = ['AGENT_TOKENS', 'team'];
const OPTIONS_KEY = ['AGENT_TOKENS', 'options'];

/**
 * Agent tokens (PER-328): the caller's own, their team's for an Owner or Admin, and what the create
 * form may offer. No list ever holds a secret: create is the one response that carries it, and the
 * screen shows it once. Writes throw the API's `{ status, message }`, so the screen can say why.
 */
export const useAgentTokens = (canManageTeam: boolean) => {
  const queryClient = useQueryClient();
  const apiClient = useClient();

  const mine = useQuery<AgentTokenPublic[]>({
    queryKey: MINE_KEY,
    queryFn: () => apiClient.get<AgentTokenPublic[]>('/agent-tokens'),
  });

  const team = useQuery<TeamAgentToken[]>({
    queryKey: TEAM_KEY,
    enabled: canManageTeam,
    queryFn: () => apiClient.get<TeamAgentToken[]>('/agent-tokens/team'),
  });

  const options = useQuery<AgentTokenOptions>({
    queryKey: OPTIONS_KEY,
    queryFn: () => apiClient.get<AgentTokenOptions>('/agent-tokens/options'),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['AGENT_TOKENS'] });

  /**
   * Resolves to the secret, which is never available again, as soon as the POST answers: the lists
   * refresh in the background rather than holding the secret back behind three refetches.
   */
  const create = async (input: NewAgentToken): Promise<string> => {
    const created = await apiClient.post<CreatedAgentToken>('/agent-tokens', input);
    void refresh();
    return created.secret;
  };

  /**
   * The passkey prompt for a create: the step-up's own challenge, issued by the API to the signed-in
   * user for this origin (not the sign-in challenge), answered by the browser. The assertion goes in
   * the create body. A 403 names the origin to use when the passkey was registered elsewhere.
   */
  const confirmWithPasskey = async (): Promise<AuthenticationResponseJSON> => {
    const optionsJSON = await apiClient.post<PublicKeyCredentialRequestOptionsJSON>('/agent-tokens/passkey-challenge');
    return startAuthentication({ optionsJSON });
  };

  /** Refreshes the lists even when the revoke fails: a 404 usually means it was revoked already. */
  const revoke = async (tokenId: number) => {
    try {
      await apiClient.delete(`/agent-tokens/${tokenId}`);
    } finally {
      await refresh();
    }
  };

  return {
    tokens: mine.data ?? [],
    isLoading: mine.isLoading,
    isError: mine.isError,
    teamTokens: team.data ?? [],
    isTeamLoading: canManageTeam && team.isLoading,
    isTeamError: canManageTeam && team.isError,
    options: options.data,
    // Not a failed refresh of options already held: the form can still use those.
    isOptionsError: options.isError && !options.data,
    create,
    confirmWithPasskey,
    revoke,
    retry: refresh,
  };
};
